import axios from "./axios";

/**
 * Offer CSV/XLSX import v2 (feature flag offer_import_v2).
 * Nested under offer_calculations — apply target is calculation items.
 * Document AI staging: fromDocumentAnalysis (also requires offer_document_ai).
 */

function normalizeOfferImportError(error, fallbackMessage) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return { ...data, status };
  }
  if (typeof data === "string" && data.trim()) {
    return { error: data, errors: [data], status };
  }
  return { error: fallbackMessage, errors: [fallbackMessage], status };
}

export async function createOfferImportJob(calculationId, file, columnMapping) {
  const form = new FormData();
  form.append("file", file);
  if (columnMapping && typeof columnMapping === "object") {
    Object.entries(columnMapping).forEach(([key, value]) => {
      if (value != null && value !== "") {
        form.append(`column_mapping[${key}]`, value);
      }
    });
  }
  return axios
    .post(`/offer_calculations/${calculationId}/import_jobs`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferImportError(error, error.message || "Import failed")
    );
}

export async function fetchOfferImportJob(calculationId, jobId) {
  return axios
    .get(`/offer_calculations/${calculationId}/import_jobs/${jobId}`)
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferImportError(error, error.message || "Failed to fetch import job")
    );
}

export async function fetchOfferImportRows(calculationId, jobId) {
  return axios
    .get(`/offer_calculations/${calculationId}/import_jobs/${jobId}/rows`)
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferImportError(error, error.message || "Failed to fetch import rows")
    );
}

export async function updateOfferImportMapping(calculationId, jobId, columnMapping) {
  return axios
    .patch(`/offer_calculations/${calculationId}/import_jobs/${jobId}`, {
      column_mapping: columnMapping,
    })
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferImportError(error, error.message || "Failed to update mapping")
    );
}

export async function updateOfferImportRow(calculationId, jobId, rowId, payload) {
  return axios
    .patch(`/offer_calculations/${calculationId}/import_jobs/${jobId}/rows/${rowId}`, payload)
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferImportError(error, error.message || "Failed to update import row")
    );
}

export async function applyOfferImportJob(calculationId, jobId, rowIds) {
  return axios
    .post(`/offer_calculations/${calculationId}/import_jobs/${jobId}/apply`, {
      row_ids: rowIds,
    })
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferImportError(error, error.message || "Failed to apply import")
    );
}

/** Stage offer_document_ai suggested_lines into the same import pipeline. */
export async function stageOfferImportFromDocumentAnalysis(
  calculationId,
  analysis,
  filename
) {
  return axios
    .post(`/offer_calculations/${calculationId}/import_jobs/from_document_analysis`, {
      analysis,
      filename,
    })
    .then((response) => response.data)
    .catch((error) =>
      normalizeOfferImportError(
        error,
        error.message || "Failed to stage document analysis"
      )
    );
}
